import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

import { API_ENDPOINT } from "../../services/api/resources";
import styles from "./PublicSubmission.module.css";

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xml",
  ".csv",
  ".jpg",
  ".jpeg",
  ".png",
];

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const DETAILS_ENDPOINT = `${API_ENDPOINT.Base_URL}/api/v1/rfq/vendor-submission-details`;
const SUBMIT_ENDPOINT = `${API_ENDPOINT.Base_URL}/api/v1/rfq/vendor-submission`;

const DEFAULT_DETAILS = {
  page_title: "Submit Information",
  page_description: "Add your comments and upload the required documents.",
  reference_number: "",
  requested_by: "",
  subject: "",
  due_date: "",
  status: "",
  vendor_name: "",
  items: [],
  allow_submission: true,
  latest_bid_token: "",
};

const getValueFromPaths = (source, paths, fallback = "") => {
  for (const path of paths) {
    const keys = path.split(".");
    let current = source;

    for (const key of keys) {
      if (current == null || typeof current !== "object" || !(key in current)) {
        current = undefined;
        break;
      }
      current = current[key];
    }

    if (current !== undefined && current !== null && current !== "") {
      return current;
    }
  }

  return fallback;
};

const getErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;

  const exactMessage = getValueFromPaths(
    responseData,
    [
      "message",
      "response",
      "detail",
      "error",
      "errors.message",
      "data.message",
    ],
    "",
  );

  if (typeof exactMessage === "string" && exactMessage.trim()) {
    return exactMessage.trim();
  }

  if (Array.isArray(exactMessage) && exactMessage.length > 0) {
    return String(exactMessage[0]);
  }

  return error?.message || fallback;
};

const formatDisplayDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const normalizeSubmissionDetails = (payload) => {
  const detailsSource =
    payload?.data ||
    payload?.response ||
    payload?.results ||
    payload ||
    {};

  const itemsRaw = getValueFromPaths(
    detailsSource,
    [
      "rfq.items",
      "items",
      "rfq_items",
      "item_list",
      "response.items",
      "data.items",
    ],
    [],
  );

  const normalizedItems = Array.isArray(itemsRaw)
    ? itemsRaw.map((item, index) => ({
        id:
          getValueFromPaths(item, ["token", "item_token", "id"], "") || `${index}`,
        name: getValueFromPaths(item, ["item_name", "name", "description"], ""),
        code: getValueFromPaths(item, ["item_code", "code"], ""),
        quantity: getValueFromPaths(item, ["quantity", "qty"], ""),
        unit: getValueFromPaths(item, ["unit", "uom"], ""),
      }))
    : [];

  return {
    page_title: getValueFromPaths(
      detailsSource,
      ["page_title", "title", "heading"],
      DEFAULT_DETAILS.page_title,
    ),
    page_description: getValueFromPaths(
      detailsSource,
      ["page_description", "description", "sub_heading", "subheading"],
      DEFAULT_DETAILS.page_description,
    ),
    reference_number: getValueFromPaths(
      detailsSource,
      [
        "reference_number",
        "rfq.rfq_code",
        "rfq_code",
        "rfq_id",
        "reference_no",
      ],
      "",
    ),
    requested_by: getValueFromPaths(
      detailsSource,
      [
        "requested_by",
        "rfq.buyer_company_name",
        "buyer_name",
        "buyer.company_name",
        "buyer_name_display",
      ],
      "",
    ),
    subject: getValueFromPaths(
      detailsSource,
      [
        "subject",
        "rfq_title",
        "rfq_name",
        "request_subject",
        "rfq.items.0.description",
      ],
      "",
    ),
    due_date: formatDisplayDate(
      getValueFromPaths(
        detailsSource,
        [
          "due_date",
          "deadline",
          "rfq.bid_to_date",
          "bid_to_date",
          "submission_deadline",
        ],
        "",
      ),
    ),
    status: getValueFromPaths(
      detailsSource,
      ["submission.status", "status", "rfq_status", "submission_status"],
      "",
    ),
    allow_submission: Boolean(
      getValueFromPaths(
        detailsSource,
        ["submission.allow_submission", "allow_submission"],
        true,
      ),
    ),
    latest_bid_token: getValueFromPaths(
      detailsSource,
      ["submission.latest_bid_token", "latest_bid_token"],
      "",
    ),
    vendor_name: getValueFromPaths(
      detailsSource,
      [
        "vendor_name",
        "vendor.company_name",
        "vendor.contact_name",
        "vendor_name_display",
      ],
      "",
    ),
    items: normalizedItems,
  };
};

export default function PublicSubmission() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [details, setDetails] = useState(null);
  const [freeText, setFreeText] = useState("");
  const [files, setFiles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const normalizedSubmissionStatus = String(details?.status || "")
    .trim()
    .toLowerCase();
  const alreadySubmitted =
    hasSubmitted ||
    normalizedSubmissionStatus === "submitted" ||
    normalizedSubmissionStatus === "success" ||
    Boolean(String(details?.latest_bid_token || "").trim());

  useEffect(() => {
    const loadDetails = async () => {
      if (!token.trim()) {
        setPageError("Submission token is missing.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPageError("");

        const response = await axios({
          method: "GET",
          url: DETAILS_ENDPOINT,
          params: { token: token.trim() },
        });

        const normalizedDetails = normalizeSubmissionDetails(response?.data);
        setDetails(normalizedDetails);
      } catch (error) {
        setPageError(
          getErrorMessage(error, "Unable to load submission details."),
        );
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [token]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const errors = [];
    const validFiles = [];

    selectedFiles.forEach((file) => {
      const extension = file.name
        .slice(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        errors.push(`${file.name}: unsupported file type.`);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: maximum file size is 10 MB.`);
        return;
      }

      validFiles.push(file);
    });

    const combinedFiles = [...files, ...validFiles];

    if (combinedFiles.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files are allowed.`);
    }

    setFiles(combinedFiles.slice(0, MAX_FILES));
    setFormError(errors.join(" "));
    setSuccessMessage("");
    event.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((currentFiles) =>
      currentFiles.filter((_, index) => index !== indexToRemove),
    );
    setFormError("");
    setSuccessMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!freeText.trim() && files.length === 0) {
      setFormError("Please enter text or upload at least one document.");
      return;
    }

    if (!token.trim()) {
      setFormError("Submission token is missing.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError("");
      setSuccessMessage("");

      const formData = new FormData();
      formData.append("token", token.trim());
      formData.append("feedback", freeText.trim());

      files.forEach((file) => {
        formData.append("documents", file);
      });

      const response = await axios({
        method: "POST",
        url: SUBMIT_ENDPOINT,
        data: formData,
      });

      const success =
        getValueFromPaths(response?.data, ["message", "response"], "") ||
        "Submitted successfully.";

      setSuccessMessage(
        `${String(success)} Please log in and check it under Submission Status.`,
      );
      setHasSubmitted(true);
      setFreeText("");
      setFiles([]);
    } catch (error) {
      setFormError(getErrorMessage(error, "Submission failed."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className={styles.page}>
        <div className={styles.stateCard}>
          <div className={styles.spinner} />
          <h2>Loading submission page</h2>
          <p>Please wait while we validate your secure link.</p>
        </div>
      </main>
    );
  }

  if (pageError || !details) {
    return (
      <main className={styles.page}>
        <div className={styles.stateCard}>
          <div className={styles.errorIcon}>!</div>
          <h2>Unable to open this page</h2>
          <p>{pageError || "Invalid submission link."}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <header className={styles.header}>
          <img
            src="https://bidz.ai/mail_assets/Logo%402x.png"
            alt="BIDZ.ai"
            className={styles.logo}
          />

          <div className={styles.headerContent}>
            <h1>{details.page_title}</h1>
            <p>{details.page_description}</p>
          </div>

          <span className={styles.secureBadge}>Secure Link</span>
        </header>

        <div className={styles.content}>
          <section className={styles.detailsSection}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionLabel}>REQUEST DETAILS</span>
                <h2>Submission information</h2>
              </div>
            </div>

            <div className={styles.detailsGrid}>
              <InfoItem label="RFQ" value={details.reference_number} />
              <InfoItem label="Buyer" value={details.requested_by} />
              <InfoItem label="Vendor" value={details.vendor_name} />
              <InfoItem label="Deadline" value={details.due_date} />
              <InfoItem label="Status" value={details.status} />
              <InfoItem label="Description" value={details.subject} />
            </div>

            {details.items.length > 0 && (
              <div className={styles.formGroup} style={{ marginTop: "18px" }}>
                <div className={styles.labelRow}>
                  <div>
                    <label>RFQ Items</label>
                    <p className={styles.helperText}>
                      Review the requested RFQ items below.
                    </p>
                  </div>
                  <span>{details.items.length} item(s)</span>
                </div>

                <div className={styles.fileList}>
                  {details.items.map((item) => (
                    <div className={styles.fileItem} key={item.id}>
                      <div className={styles.fileIcon}>ITEM</div>

                      <div className={styles.fileDetails}>
                        <strong>{item.name || "-"}</strong>
                        <span>
                          Code: {item.code || "-"} | Quantity: {item.quantity || "-"}
                          {item.unit ? ` ${item.unit}` : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <div className={styles.divider} />

          {successMessage ? (
            <div className={styles.successMessage}>
              <strong className={styles.boldText}>Submission completed.</strong>{" "}
              <span className={styles.regularText}>{successMessage}</span>
            </div>
          ) : alreadySubmitted || !details.allow_submission ? (
            <div className={styles.successMessage}>
              <strong className={styles.boldText}>Submission already recorded.</strong>{" "}
              <span className={styles.regularText}>
                Please log in and check it under Submission Status.
              </span>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles.sectionHeader}>
                <div>
                  <span className={styles.sectionLabel}>YOUR SUBMISSION</span>
                  <h2>Upload Documents </h2>
                </div>

                <span className={styles.requiredNote}>
                  Text or document is required
                </span>
              </div>

              <div className={styles.formGroup}>
                <div className={styles.labelRow}>
                  <div>
                    <label htmlFor="documents">Upload Documents</label>

                    <p className={styles.helperText}>
                      PDF, Word, Excel, CSV, JPG, and PNG are allowed.
                    </p>
                  </div>

                  <span>
                    {files.length}/{MAX_FILES}
                  </span>
                </div>

                <label htmlFor="documents" className={styles.uploadBox}>
                  <span className={styles.uploadIcon}>↑</span>
                  <strong>Choose or drop documents here</strong>
                  <small>Maximum 10 MB per file</small>
                </label>

                <input
                  id="documents"
                  type="file"
                  multiple
                  hidden
                  disabled={submitting}
                  accept={ALLOWED_EXTENSIONS.join(",")}
                  onChange={handleFileChange}
                />

                {files.length > 0 && (
                  <div className={styles.fileList}>
                    {files.map((file, index) => (
                      <div
                        className={styles.fileItem}
                        key={`${file.name}-${file.size}-${index}`}
                      >
                        <div className={styles.fileIcon}>FILE</div>

                        <div className={styles.fileDetails}>
                          <strong>{file.name}</strong>
                          <span>{formatFileSize(file.size)}</span>
                        </div>

                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => removeFile(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* <div className={styles.formGroup}>
                <div className={styles.labelRow}>
                  <label htmlFor="freeText">Message / Feedback / Notes</label>

                  <span>{freeText.length}/1500</span>
                </div>

                <textarea
                  id="freeText"
                  rows="6"
                  maxLength={1500}
                  value={freeText}
                  disabled={submitting}
                  placeholder="Enter your message, feedback, clarification, or additional details..."
                  onChange={(event) => {
                    setFreeText(event.target.value);
                    setFormError("");
                    setSuccessMessage("");
                  }}
                  className={styles.textarea}
                />
              </div> */}

              {formError && <div className={styles.errorMessage}>{formError}</div>}

              <div className={styles.actions}>
                <p>
                  The submitted information will be securely stored in BIDZ.ai.
                </p>

                <button
                  type="submit"
                  disabled={submitting}
                  className={styles.submitButton}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className={styles.infoItem}>
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
